import React, { useState, useEffect, useRef, MouseEvent } from "react";
import styles from "./styles.module.scss";
import DeleteMenu from "../../../../public/images/delete-icon.svg";
import EditMenu from "../../../../public/images/edit-menu-icon.svg";
import ActivateMenu from "../../../../public/images/toggle.svg";
import OnlinePayment from "../../../../public/images/online-payment.svg";
import OfflinePayment from "../../../../public/images/offline-payment.svg";
import reorderIcon from "../../../../public/images/ico_reorder.svg";
import ToolTip from "app/routes/Scheduling/ToolTip";
import { cls } from "app/utils";
import { useTranslation } from "react-i18next";
import { LessonRateModel } from "app/models/scheduling/LessonRateModel";
import { useWindowWidth } from "app/utils/hooks/useWindowWidth";
import { formatDurationLabel } from "app/utils/timeUtils";
import ExpandableDescription from "app/components/ExpandableDescription";
import CalendarColorDot from "app/components/CalendarColorDot";

interface BookingOptionsViewProps {
  list: LessonRateModel[];
  onEdit: (item: LessonRateModel) => void;
  onRemove: (item: LessonRateModel) => void;
  onChangeStatus: (item: LessonRateModel | null) => void;
  saveNewStatus: (item: LessonRateModel) => void;
  currencySymbol: string;
  showBookingsLimit?: boolean;
  isReorderMode?: boolean;
  dragHandleProps?: any;
}

const BookingOptionsView: React.FC<BookingOptionsViewProps> = ({
  list,
  onEdit,
  onRemove,
  onChangeStatus,
  saveNewStatus,
  currencySymbol,
  isReorderMode = false,
  dragHandleProps,
}) => {
  const { t } = useTranslation();
  const [menuVisible, setMenuVisible] = useState<number | null>(null);
  const actionMenuRef = useRef<HTMLDivElement>(null);
  const [shouldShowTooltip, setShouldShowTooltip] = useState<Record<number, boolean>>({});
  const windowWidth = useWindowWidth();

  const handleArrowClick = (index: number) => {
    setMenuVisible(menuVisible === index ? null : index);
  };

  const handleEdit = (item: LessonRateModel) => {
    onEdit(item);
    setMenuVisible(null);
  };

  const handleRemove = (item: LessonRateModel) => {
    onRemove(item);
    setMenuVisible(null);
  };

  const handleChangeStatus = (item: LessonRateModel) => {
    if (item.defaultRate === true && item.active === true) {
      const count = list.filter((rate) => rate.active === true).length;

      if (count === 1) {
        // Last active rate - show warning about deactivating all
        onChangeStatus(item);
      } else {
        // Default rate but other active rates exist - show "select new default" message
        onChangeStatus(null);
      }
    } else {
      // Normal toggle
      item.active = !item.active;
      saveNewStatus(item);
    }

    setMenuVisible(null);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      actionMenuRef.current &&
      !actionMenuRef.current.contains(event.target as Node)
    ) {
      setMenuVisible(null);
    }
  };

  useEffect(() => {
    document.addEventListener(
      "mousedown",
      handleClickOutside as unknown as EventListener
    );
    return () => {
      document.removeEventListener(
        "mousedown",
        handleClickOutside as unknown as EventListener
      );
    };
  }, []);

  const checkTextWidth = () => {
    const textMaxWidth = windowWidth >= 1150 ? 150 : 100;

    const visibility: Record<number, boolean> = {};
    list.forEach((_, index) => {
      const element = document.getElementById(`booking-title-${index}`);
      if (element) {
        visibility[index] = element.scrollWidth > textMaxWidth;
      }
    });
    setShouldShowTooltip(visibility);
  };

  useEffect(() => {
    checkTextWidth();
  }, [list, windowWidth]);

  const getDurationLabel = (value: string) => formatDurationLabel(value);

  return (
    <>
      {list &&
        list.map(
          (item, index) =>
            !(item as LessonRateModel & { _destroy?: boolean })._destroy && (
              <div key={index} className={isReorderMode ? styles.rowPricingViewReorder : styles.rowPricingView}>
                {isReorderMode && (
                  <div className={styles.drag_handle} {...dragHandleProps}>
                    <img
                      src={reorderIcon}
                      alt="reorder"
                      className={styles.reorder_icon}
                    />
                  </div>
                )}
                <div className={styles.cell}>
                  <div
                    className={`${styles.textWithTooltip} ${
                      item.defaultRate ? styles.defaultRateContainer : ""
                    }`}
                  >
                    <div className={styles.titleRow}>
                      {/* The colour the calendar draws this option's sessions in. */}
                      <CalendarColorDot color={item.color} testId={`booking-option-color-${index}`} />
                      <div
                        id={`booking-title-${index}`}
                        className={styles.truncatedText}
                      >
                        {item.title}
                      </div>
                      {shouldShowTooltip[index] && (
                        <div className={styles.toolTipPosition}>
                          <ToolTip selected={true} text={item.title} orientation={"right"} />
                        </div>
                      )}
                    </div>
                  </div>
                  <ExpandableDescription text={item.description} />
                </div>
                <div className={styles.cell}>
                  {getDurationLabel(item.duration || item.durationInMinutes.toString())}
                </div>
                <div className={styles.cell}>
                  {item.rate !== 0 ? `${currencySymbol}${item.rate}` : ""}
                </div>
                <div className={styles.cell}>
                  <div className={styles.truncatedLocation} title={item.location}>
                    {item.location}
                  </div>
                </div>

                <div className={styles.cell}>
                  <div
                    className={cls({
                      [styles.activeButtonDesign]: item.active,
                      [styles.inactiveButtonDesign]: !item.active,
                    })}
                  >
                    {item.active ? t("Active") : t("Inactive")}
                  </div>
                </div>
                <div className={`${styles.cell} ${styles.hide_on_desktop}`} />

                <div className={`${styles.cell} ${styles.payment_cell}`}>
                  <div className={styles.collect_icons}>
                    {(item.collectType === "online" || item.collectType === "both") && (
                      <div className={styles.iconWithTooltip}>
                        <img
                          src={OnlinePayment}
                          alt="Online Payment icon"
                          className={styles.moneyIcon}
                        />
                        <span className={styles.tooltipText}>{t("Collect Online")}</span>
                      </div>
                    )}
                    {(item.collectType === "offline" || item.collectType === "both") && (
                      <div className={styles.iconWithTooltip}>
                        <img
                          src={OfflinePayment}
                          alt="Offline Payment icon"
                          className={styles.moneyIcon}
                        />
                        <span className={styles.tooltipText}>{t("Collect Offline")}</span>
                      </div>
                    )}
                    {item.collectType === "only_package" && (
                      <div className={styles.iconWithTooltip}>
                        <i className={`ico ico-dollar-circle ${styles.moneyIcon}`} />
                        <span className={styles.tooltipText}>{t("Package Only")}</span>
                      </div>
                    )}
                  </div>
                </div>

                {!isReorderMode && (
                  <div className={`${styles.cell} ${styles.iconContainer}`}>
                    <div
                      className={cls("bullets", styles.cursorPointer)}
                      onClick={() => handleArrowClick(index)}
                    />
                    {menuVisible === index && (
                      <div ref={actionMenuRef} className={styles.actionMenu}>
                        <button
                          onClick={() => handleChangeStatus(item)}
                          className={styles.editButton}
                        >
                          <img src={ActivateMenu} alt="activateMenu" />
                          <div style={{ marginLeft: "10px" }}>
                            {item.active ? t("Deactivate") : t("Activate")}
                          </div>
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className={styles.editButton}
                        >
                          <img src={EditMenu} alt="editMenu" />
                          <div style={{ marginLeft: "10px" }}>{t("Edit")}</div>
                        </button>
                        <button
                          onClick={() => handleRemove(item)}
                          className={styles.removeButton}
                        >
                          <img
                            src={DeleteMenu}
                            alt="deleteMenu"
                            className={styles.deleteImage}
                          />
                          <div style={{ marginLeft: "10px" }}>{t("Delete")}</div>
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )
        )}
    </>
  );
};

export default BookingOptionsView;
